import type { Cell, Header, Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import { Button } from "@/shared/ui/button";

import { Skeleton } from "../skeleton";

import type { DataTableProps } from "@/shared/types";
import { SortingButton } from "@/features/SortingButton/SortingButton.tsx";
import { ActiveFilters } from "@/features/ActiveFilters";

export function MobileDataTable<TData, TValue>({
  data,
  isLoading,
  table,
  page,
  setPage,
  pageCount,
  selectMobileColumn,
  refetch,
}: Omit<DataTableProps<TData, TValue>, "columns"> & { table: Table<TData> }) {
  const handlePreviousPage = () => {
    if (setPage && page) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (setPage && page) {
      setPage(page + 1);
    }
  };

  if (data?.length === 0) {
    return (
      <>
        {refetch && <ActiveFilters refetch={refetch} />}

        <div className="py-4 text-center text-gray-500" role="status">
          {isLoading ? <Skeleton /> : <span>Ничего нет.</span>}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4">
      <SortingButton tableHeader={table.getHeaderGroups()} />
      {refetch && <ActiveFilters refetch={refetch} />}
      {isLoading ? (
        <div className="space-y-2" role="status" aria-label="Loading data">
          {Array.from({ length: 9 }, (_, index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-md bg-gray-200"
            />
          ))}
        </div>
      ) : (
        <Accordion type="single" collapsible className="space-y-2">
          {table.getRowModel().rows.map((row) => (
            <MobileDataTableItem
              key={row.id}
              id={row.id}
              headers={table.getHeaderGroups()[0].headers}
              rows={row.getVisibleCells()}
              tableHeader={row.getVisibleCells()[selectMobileColumn ?? 0]}
            />
          ))}
        </Accordion>
      )}

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant={page === 1 ? "outline" : "default"}
          className={`h-[36px] w-[106px] ${page === 1 && "text-textColor"}`}
          onClick={handlePreviousPage}
          disabled={page === 1}
          aria-label="Предыдущая страница"
        >
          Назад
        </Button>
        {pageCount ? (
          <p className="text-center text-sm text-textColor">
            Страница {page} из {pageCount}
          </p>
        ) : null}
        <Button
          variant={page === pageCount ? "outline" : "default"}
          className={`h-[36px] w-[106px] ${page === pageCount && "text-textColor"}`}
          onClick={handleNextPage}
          disabled={page === pageCount}
          aria-label="Следующая страница"
        >
          Вперед
        </Button>
      </div>
    </div>
  );
}

interface MobileDataTableItemProps<TData> {
  headers: Header<TData, unknown>[];
  rows: Cell<TData, unknown>[];
  tableHeader: Cell<TData, unknown>;
  id: string;
}

const MobileDataTableItem = ({
  headers,
  rows,
  id,
  tableHeader,
}: MobileDataTableItemProps<any>) => {
  const zip = <T, U>(a: T[], b: U[]) => a.map((k, i) => [k, b[i]] as [T, U]);
  return (
    <AccordionItem
      value={`item-${id}`}
      className="rounded border-b border-white/15 px-2 shadow backdrop-blur-md"
    >
      <AccordionTrigger className="flex w-full items-center justify-between py-4">
        <div className="flex min-w-[150px] max-w-[250px] items-start justify-between self-center text-ellipsis whitespace-nowrap text-textColor">
          {flexRender(
            tableHeader.column.columnDef.cell,
            tableHeader.getContext(),
          )}
        </div>
      </AccordionTrigger>

      <AccordionContent>
        <div className="rounded-lg bg-bgColor">
          {zip(rows, headers).map(([row, header]) => (
            <div
              key={row.id}
              className="flex w-full items-center justify-between px-2 py-3 text-textColor"
            >
              <div className="text-textColor">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </div>
              <div>
                {flexRender(row.column.columnDef.cell, row.getContext())}
              </div>
            </div>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
